class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.backdrop = this.querySelector('[data-cart-backdrop]');
    this.body = this.querySelector('[data-cart-drawer-refresh]');
    this.openTriggers = document.querySelectorAll('[data-cart-open]');

    this.openTriggers.forEach((trigger) => trigger.addEventListener('click', () => this.open()));
    this.bindCloseTriggers();
    this.backdrop.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });

    // Same back/forward-cache guard as the mobile menu: a restored page must never come
    // back with the scroll lock still applied, or it looks frozen until a reload.
    window.addEventListener('pageshow', () => this.close());

    this.body.addEventListener('click', (event) => this.handleBodyClick(event));
    this.body.addEventListener('change', (event) => this.handleBodyChange(event));

    document.addEventListener('cart:refresh', (event) => {
      const openDrawer = event.detail && event.detail.openDrawer;
      this.render().then(() => {
        if (openDrawer) this.open();
      });
    });
  }

  bindCloseTriggers() {
    this.querySelectorAll('[data-cart-close]').forEach((trigger) => {
      trigger.addEventListener('click', () => this.close());
    });
  }

  open() {
    this.classList.add('is-open');
    this.backdrop.dataset.open = 'true';
    document.documentElement.classList.add('scroll-locked');
    document.body.classList.add('scroll-locked');
  }

  close() {
    this.classList.remove('is-open');
    this.backdrop.dataset.open = 'false';
    document.documentElement.classList.remove('scroll-locked');
    document.body.classList.remove('scroll-locked');
  }

  handleBodyClick(event) {
    const increaseBtn = event.target.closest('[data-quantity-increase]');
    const decreaseBtn = event.target.closest('[data-quantity-decrease]');
    const removeBtn = event.target.closest('[data-cart-remove]');

    if (increaseBtn || decreaseBtn) {
      const wrapper = (increaseBtn || decreaseBtn).closest('[data-quantity-input]');
      const input = wrapper.querySelector('[data-quantity-value]');
      const line = Number(wrapper.dataset.line);
      let value = Number(input.value) || 0;
      value = increaseBtn ? value + 1 : Math.max(0, value - 1);
      input.value = value;
      this.changeLine(line, value);
      return;
    }

    if (removeBtn) {
      this.changeLine(Number(removeBtn.dataset.line), 0);
    }
  }

  handleBodyChange(event) {
    const input = event.target.closest('[data-quantity-value]');
    if (!input) return;
    const wrapper = input.closest('[data-quantity-input]');
    const line = Number(wrapper.dataset.line);
    const value = Math.max(0, Number(input.value) || 0);
    this.changeLine(line, value);
  }

  async changeLine(line, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      await this.render();
    } catch (error) {
      /* the server-rendered cart form still works as a no-JS fallback on submit */
    }
  }

  async render() {
    try {
      const [sectionResponse, cartResponse] = await Promise.all([
        fetch(`${window.location.origin}/?sections=cart-drawer`),
        fetch('/cart.js'),
      ]);
      const sectionData = await sectionResponse.json();
      const cart = await cartResponse.json();
      const html = sectionData['cart-drawer'];

      if (html) {
        const template = document.createElement('template');
        template.innerHTML = html;
        const newBody = template.content.querySelector('[data-cart-drawer-refresh]');
        if (newBody) this.body.innerHTML = newBody.innerHTML;
      }
      this.updateCartCount(cart.item_count);
      this.bindCloseTriggers();
    } catch (error) {
      /* leave existing drawer contents in place if the refresh request fails */
    }
  }

  updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-count-mobile]').forEach((el) => {
      el.textContent = `(${count})`;
      el.hidden = count === 0;
    });
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawer);
}

/* Shared helper other components (e.g. the product add-to-cart form) call after a
   successful /cart/add.js request, so the drawer refreshes without knowing about them. */
window.MDockCart = {
  refresh(options = {}) {
    document.dispatchEvent(new CustomEvent('cart:refresh', { detail: options }));
  },
};

/* The standalone /cart page: same quantity/remove interactions as the drawer, but a full
   reload after each change is simplest and always correct since this page has no drawer
   markup to patch in place. */
class CartPage extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('change', (event) => this.handleChange(event));
  }

  handleClick(event) {
    const increaseBtn = event.target.closest('[data-quantity-increase]');
    const decreaseBtn = event.target.closest('[data-quantity-decrease]');
    const removeBtn = event.target.closest('[data-cart-remove]');

    if (increaseBtn || decreaseBtn) {
      const wrapper = (increaseBtn || decreaseBtn).closest('[data-quantity-input]');
      const input = wrapper.querySelector('[data-quantity-value]');
      const line = Number(wrapper.dataset.line);
      let value = Number(input.value) || 0;
      value = increaseBtn ? value + 1 : Math.max(0, value - 1);
      input.value = value;
      this.changeLine(line, value);
      return;
    }

    if (removeBtn) {
      this.changeLine(Number(removeBtn.dataset.line), 0);
    }
  }

  handleChange(event) {
    const input = event.target.closest('[data-quantity-value]');
    if (!input) return;
    const wrapper = input.closest('[data-quantity-input]');
    const line = Number(wrapper.dataset.line);
    const value = Math.max(0, Number(input.value) || 0);
    this.changeLine(line, value);
  }

  async changeLine(line, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity }),
      });
      window.location.reload();
    } catch (error) {
      /* the server-rendered cart form still works as a no-JS fallback on submit */
    }
  }
}

if (!customElements.get('cart-page-component')) {
  customElements.define('cart-page-component', CartPage);
}
